import {
  CHAT_ATTACHMENT_MAX_BYTES,
  CHAT_ATTACHMENT_MAX_LABEL,
  CHAT_ATTACHMENTS_PER_MESSAGE,
  CHAT_TEXT_EXTENSIONS_LABEL,
  type ChatImageType,
  isChatImageType,
  textTypeForName,
} from '@af/shared';

export const isImageType = (t: string): t is ChatImageType => isChatImageType(t);

/** A text/log file is recognised by its extension (browsers report '' for .ndjson and .log). */
export const isTextFile = (file: { name: string; type: string }) => textTypeForName(file.name) !== null;

/** Human-readable size ("12.3 KB"). */
export function formatFileSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** Why a file cannot be attached (null = fine). Shared by the button, paste and drag & drop. */
export function fileProblem(file: { name: string; type: string; size: number }): string | null {
  const name = file.name || 'This file';
  if (!isImageType(file.type) && !isTextFile(file))
    return `${name} is not supported: attach a PNG, JPEG, GIF or WebP image, or a ${CHAT_TEXT_EXTENSIONS_LABEL} file.`;
  if (file.size === 0) return `${name} is empty.`;
  if (file.size > CHAT_ATTACHMENT_MAX_BYTES) return `${name} is larger than ${CHAT_ATTACHMENT_MAX_LABEL}.`;
  return null;
}

/**
 * Split picked/pasted/dropped files into the ones to prepare and error messages. `taken` is the number of
 * files already in the draft (including ones still being prepared).
 */
export function planAttach(
  files: { name: string; type: string; size: number }[],
  taken: number,
): { accepted: number[]; errors: string[] } {
  const errors: string[] = [];
  const accepted: number[] = [];
  let tooMany = false;
  files.forEach((f, i) => {
    const problem = fileProblem(f);
    if (problem) errors.push(problem);
    else if (taken + accepted.length >= CHAT_ATTACHMENTS_PER_MESSAGE) tooMany = true;
    else accepted.push(i);
  });
  if (tooMany) errors.push(`At most ${CHAT_ATTACHMENTS_PER_MESSAGE} files per message.`);
  return { accepted, errors };
}
